import React, { useState } from "react";
import {
  Col,
  Row,
  Button,
  Layout,
  Typography,
  Descriptions,
  Image,
  Modal,
  message,
} from "antd";
import dayjs from "dayjs";
import { FaArrowLeft, FaEdit, FaTrash } from "react-icons/fa";
import { ASSETS, UPLOAD_URL, BASE_URL } from "../../config/constants";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import axios from "axios";

const { Title, Text, Paragraph } = Typography;

function AssetDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const token = useSelector((state) => state.user.userToken);
  const asset = location.state?.asset ?? null;
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!asset) {
    return (
      <Layout className="configuration">
        <div className="boxDetails2" style={{ padding: 40 }}>
          <p>No asset data. Please open this page from the Asset list (View).</p>
          <Button type="primary" className="loginButton" onClick={() => navigate("/asset-management")}>
            Back to Assets
          </Button>
        </div>
      </Layout>
    );
  }
  const handleDelete = () => {
    setDeleting(true);
    axios
      .delete(BASE_URL + ASSETS.deleteAsset + id, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res?.data ?? res;
        setDeleting(false);
        setDeleteModalOpen(false);
        if (data?.status) {
          swal("Success!", "Asset deleted", "success");
          navigate("/asset-management");
        } else {
          swal("Error", data?.message || "Delete failed", "error");
        }
      })
      .catch(() => {
        setDeleting(false);
        setDeleteModalOpen(false);
        message.error("Delete failed");
      });
  };


  const createdBy = asset.createdBy;
  const createdByName = createdBy
    ? [createdBy.firstName, createdBy.lastName].filter(Boolean).join(" ") || createdBy.email
    : "—";
  const createdByEmail = createdBy?.email || "";

  const courses = asset.courses || [];

  return (
    <Layout className="configuration">
      <div className="boxDetails2">
        <Row style={{ padding: "10px 20px" }}>
          <Col xs={24} md={12} style={{ display: "flex", alignItems: "center" }}>
            <FaArrowLeft
              style={{ fontWeight: "bold", fontSize: "20px", cursor: "pointer" }}
              onClick={() => navigate(-1)}
            />
            &emsp;
            <h1 className="pageTitle" style={{ margin: 0 }}>Asset Details</h1>
          </Col>
          <Col xs={24} md={12} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
            <Button type="primary" className="loginButton" icon={<FaEdit />} onClick={() => navigate(`/asset-management/${id}/edit`, { state: { asset } })}>
              Edit
            </Button>
            <Button danger icon={<FaTrash />} onClick={() => setDeleteModalOpen(true)}>
              Delete
            </Button>
          </Col>
        </Row>
        <br />

        <Row style={{ padding: "20px" }}>
          <Col xs={24}>
            <Title level={4}>{asset.title}</Title>
            {asset.description && <Paragraph>{asset.description}</Paragraph>}

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Type">{asset.type || "—"}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Text strong style={{ textTransform: "uppercase" }}>{asset.status ?? "—"}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Created By">
                {createdByName}
                {createdByEmail && <span> ({createdByEmail})</span>}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {asset.createdAt ? dayjs(asset.createdAt).format("MMM D, YYYY h:mm A") : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {asset.updatedAt ? dayjs(asset.updatedAt).format("MMM D, YYYY h:mm A") : "—"}
              </Descriptions.Item>
            </Descriptions>

            {asset.fileUrl && (
              <>
                <Title level={5} style={{ marginTop: 24 }}>Preview</Title>
                {asset.type === "IMAGE" && (
                  <Image
                    src={UPLOAD_URL + asset.fileUrl}
                    alt={asset.title}
                    style={{ maxWidth: 400, maxHeight: 300, objectFit: "contain" }}
                  />
                )}
                {asset.type === "VIDEO" && (
                  <video
                    src={UPLOAD_URL + asset.fileUrl}
                    controls
                    style={{ maxWidth: "100%", maxHeight: 400 }}
                  />
                )}
                {asset.type === "PDF" && (
                  <p>
                    <a href={UPLOAD_URL + asset.fileUrl} target="_blank" rel="noopener noreferrer">
                      Open PDF
                    </a>
                  </p>
                )}
                {!["IMAGE", "VIDEO", "PDF"].includes(asset.type) && (
                  <a href={UPLOAD_URL + asset.fileUrl} target="_blank" rel="noopener noreferrer">
                    Open file
                  </a>
                )}
              </>
            )}

            {courses.length > 0 && (
              <>
                <Title level={5} style={{ marginTop: 24 }}>Courses</Title>
                <Row gutter={[8, 8]}>
                  {courses.map((c) => {
                    const courseId = typeof c === "object" ? c._id : c;
                    const title = typeof c === "object" ? c.title || c.name : c;
                    return (
                      <Col key={courseId}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => navigate(`/course-details/${courseId}`)}
                        >
                          {title || courseId}
                        </Button>
                      </Col>
                    );
                  })}
                </Row>
              </>
            )}
          </Col>
        </Row>
      </div>
      <br />
      <br />

      <Modal
        title="Delete Asset"
        open={deleteModalOpen}
        onOk={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        confirmLoading={deleting}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete this asset?
      </Modal>
    </Layout>
  );
}

export default AssetDetails;
